import React, { FC, memo, useCallback, useMemo } from 'react';
import { useField } from 'formik';

import usePrefix from 'utils/usePrefix';
import useFocusNewItem from 'utils/useFocusNewItem';
import Button from 'components/Button';
import { INPUT_ITEMS_LIMIT } from 'components/ResqlExpert/constants';
import FormItemText from 'components/ResqlExpert/FormItemText';
import FormDescription from 'components/ResqlExpert/FormDescription';

import {
  FormListActionsStyled,
  FormListStyled,
  FormSectionStyled,
  TabBodyContentStyled,
} from './styles';

interface Props {
  fieldName: string;
  group: string;
  titleKey: string;
  hintKey: string;
}

const EditableListTab: FC<Props> = ({ fieldName, group, titleKey, hintKey }) => {
  const t = usePrefix('ResqlExpert');
  const [field, , helpers] = useField<string[]>(fieldName);

  const stored = field.value ?? [];
  const items = useMemo(() => (stored.length > 0 ? stored : ['']), [stored]);

  const lastValue = items[items.length - 1] ?? '';
  const isAddAvailable = lastValue.trim().length > 0 && items.length < INPUT_ITEMS_LIMIT;

  const onAdd = useCallback(() => {
    helpers.setValue([...items, '']);
  }, [helpers, items]);

  const onChange = useCallback(
    (index: number, val: string) => {
      const next = [...items];
      next[index] = val;
      helpers.setValue(next);
    },
    [helpers, items],
  );

  const onRemove = useCallback(
    (index: number) => {
      helpers.setValue(items.filter((_, i) => i !== index));
    },
    [helpers, items],
  );

  useFocusNewItem(items.length, (index) => `${group}-${index}`);

  return (
    <TabBodyContentStyled>
      <FormSectionStyled>
        <FormDescription title={t(titleKey)}>
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{ __html: t(hintKey) }} />
        </FormDescription>
        <FormListStyled>
          {items.map((value, index) => (
            <FormItemText
              key={index}
              group={group}
              index={index}
              label={t(titleKey)}
              placeholder={t(titleKey)}
              value={value}
              onChange={onChange}
              onRemove={onRemove}
            />
          ))}
        </FormListStyled>
        <FormListActionsStyled>
          <Button
            label={t('ctaAdd')}
            color="primary"
            onClick={onAdd}
            disabled={!isAddAvailable}
          />
        </FormListActionsStyled>
      </FormSectionStyled>
    </TabBodyContentStyled>
  );
};

export default memo(EditableListTab);
